import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSite } from "../context/SiteContext";
type MetaRecord = {
  title: string;
  description: string;
};
const englishMeta: Record<string, MetaRecord> = {
  "/": {
    title: "Agave Family | Mexican Food in Teton Valley",
    description: "Authentic Mexican food, handcrafted cocktails, and warm Teton Valley hospitality at Agave in Driggs and Blue Agave in Victor."
  },
  "/locations": {
    title: "Agave Family Locations | Driggs & Victor, Idaho",
    description: "Choose Agave Family Mexican Restaurant in Driggs or Blue Agave in Victor and find the correct menu, phone number, and directions."
  },
  "/driggs": {
    title: "Agave Family Mexican Restaurant | Driggs, ID | Teton Valley",
    description: "Visit Agave Family Mexican Restaurant in Driggs, Idaho for authentic Mexican food, sizzling fajitas, mole enchiladas, seafood, lunch, and family dining in Teton Valley."
  },
  "/victor": {
    title: "Blue Agave | Mexican Kitchen & Bar in Victor, ID",
    description: "Visit Blue Agave in Victor, Idaho for authentic Mexican food, sizzling fajitas, seafood, margaritas, family dining, and local events in Teton Valley."
  },
  "/menus": {
    title: "Menus by Location | Agave Family",
    description: "Choose the correct Driggs or Victor menu to see exact location-specific dishes and prices."
  },
  "/menus/driggs": {
    title: "Driggs Menu & Exact Prices | Agave Family",
    description: "View the current digital menu and exact listed prices for Agave Family Mexican Restaurant in Driggs, Idaho."
  },
  "/menus/victor": {
    title: "Victor Menu & Exact Prices | Blue Agave",
    description: "View the current digital menu and exact listed prices for Blue Agave in Victor, Idaho."
  },
  "/drinks": {
    title: "Handcrafted Cocktails | Agave Family",
    description: "Explore the handcrafted cocktail experience at Agave Family restaurants in Teton Valley."
  },
  "/events": {
    title: "Events in Teton Valley | Agave Family",
    description: "Discover confirmed events at Agave Family Mexican Restaurant in Driggs and Blue Agave in Victor."
  },
  "/catering": {
    title: "Mexican Catering in Teton Valley | Agave Family",
    description: "Request Agave Family catering for weddings, celebrations, business events, community gatherings, and large food orders."
  },
  "/private-events": {
    title: "Private Events | Agave Family",
    description: "Ask about hosting a group dinner, celebration, business gathering, or cultural event with Agave Family."
  },
  "/our-story": {
    title: "Our Story | Agave Family",
    description: "Discover the warm Mexican flavors and Teton Valley hospitality shared by Agave Family restaurants in Driggs and Victor."
  },
  "/gallery": {
    title: "Food, Drinks & Community Gallery | Agave Family",
    description: "Explore real food, drinks, restaurant, event, community, and catering photography from Agave Family."
  },
  "/contact": {
    title: "Contact Agave Family | Driggs & Victor",
    description: "Find the correct address, phone number, and directions for Agave Family in Driggs or Blue Agave in Victor."
  },
  "/privacy": {
    title: "Privacy Policy | Agave Family",
    description: "Privacy information for the Agave Family website."
  },
  "/terms": {
    title: "Terms | Agave Family",
    description: "Website terms for Agave Family."
  },
  "/accessibility": {
    title: "Accessibility | Agave Family",
    description: "Agave Family website accessibility information and contact options."
  }
};
function setMeta(name: string, content: string, property = false) {
  const attribute = property ? "property" : "name";
  let element = document.head.querySelector(`meta[${attribute}="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}
export function SiteMeta() {
  const {
    pathname
  } = useLocation();
  const {
    language
  } = useSite();
  useEffect(() => {
    const fallback = {
      title: "Page Not Found | Agave Family",
      description: "Return to Agave Family to choose a Teton Valley restaurant location."
    };
    const english = englishMeta[pathname] ?? fallback;
    const title = language === "es" ? `${english.title} — Español` : english.title;
    document.title = title;
    setMeta("description", english.description);
    setMeta("og:title", title, true);
    setMeta("og:description", english.description, true);
    setMeta("og:type", "website", true);
    setMeta("og:image", "https://cdn.magicpatterns.com/uploads/wk6Bwx4nrP4caACt6R4SpH/2026-08-19-14-56-02-688.png", true);
    setMeta("twitter:card", "summary_large_image");
    let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${window.location.origin}${pathname}`;
  }, [language, pathname]);
  return null;
}